import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    requestAnimationFrame(() => this.#setupThumbnailInteraction());
  }

  #controller = new AbortController();
  #committedIndex = 0;
  #previewIndex = null;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail) return;

        const { html } = detail;
        const featuredMediaSrc = detail.resource?.featured_media?.preview_image?.src || detail.resource?.featured_image?.src;
        if (!featuredMediaSrc) return;

        const newMediaGallery = html?.querySelector('media-gallery');
        const activeGallery = newMediaGallery || this;

        if (newMediaGallery) this.replaceWith(newMediaGallery);

        requestAnimationFrame(() => activeGallery.selectVariantMedia(detail.resource));
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Selects a variant's Shopify featured media without relying on product-specific identifiers.
   * If a variant has no featured media, the customer's current gallery position is preserved.
   *
   * @param {{ featured_media?: { preview_image?: { src?: string } }, featured_image?: { src?: string } } | null} resource
   */
  selectVariantMedia(resource) {
    const featuredMediaSrc = resource?.featured_media?.preview_image?.src || resource?.featured_image?.src;
    if (!featuredMediaSrc) return;

    const featuredPath = this.#mediaPath(featuredMediaSrc);
    if (!featuredPath) return;

    const slides = Array.from(this.querySelectorAll('slideshow-slide'));
    const slideIndex = slides.findIndex((slide) => this.#containsMediaPath(slide, featuredPath));
    if (slideIndex >= 0) this.#selectMedia(slideIndex, true);

    if (this.presentation === 'grid') {
      const grid = this.querySelector('.media-gallery__grid');
      const gridItems = Array.from(grid?.children || []);
      const featuredItem = gridItems.find((item) => this.#containsMediaPath(item, featuredPath));
      if (grid && featuredItem && featuredItem !== grid.firstElementChild) grid.prepend(featuredItem);
    }
  }

  /** @param {Element} element @param {string} mediaPath */
  #containsMediaPath(element, mediaPath) {
    return Array.from(element.querySelectorAll('img')).some((image) => this.#mediaPath(image.currentSrc || image.src) === mediaPath);
  }

  /** @param {string} src */
  #mediaPath(src) {
    try {
      return new URL(src, window.location.origin).pathname;
    } catch {
      return '';
    }
  }

  /**
   * Adds Amazon-style image preview behavior without replacing Horizon's
   * native slideshow, zoom, touch, or variant-media handling.
   */
  #setupThumbnailInteraction() {
    const rail = this.querySelector('slideshow-controls[thumbnails]');
    if (!rail) return;

    const thumbnails = Array.from(rail.querySelectorAll('.slideshow-controls__thumbnail'));
    const slides = Array.from(this.querySelectorAll('slideshow-slide'));
    const { signal } = this.#controller;

    const preview = (index, pointerEvent) => {
      if (pointerEvent && !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
      if (!slides[index]?.classList.contains('product-media-container--image')) return;

      this.#previewIndex = index;
      this.#selectMedia(index, false);
    };

    const restoreCommittedMedia = () => {
      if (this.#previewIndex === null) return;

      this.#previewIndex = null;
      this.#selectMedia(this.#committedIndex, false);
    };

    thumbnails.forEach((thumbnail, index) => {
      thumbnail.addEventListener('pointerenter', (event) => preview(index, event), { signal });
      thumbnail.addEventListener('focus', () => preview(index), { signal });
      thumbnail.addEventListener(
        'click',
        () => {
          this.#committedIndex = index;
          this.#previewIndex = null;
        },
        { signal }
      );
    });

    rail.addEventListener('pointerleave', restoreCommittedMedia, { signal });
    rail.addEventListener(
      'focusout',
      (event) => {
        if (!(event.relatedTarget instanceof Node) || !rail.contains(event.relatedTarget)) restoreCommittedMedia();
      },
      { signal }
    );
  }

  /** @param {number} index @param {boolean} commit */
  #selectMedia(index, commit) {
    this.slideshow?.select(index, undefined, { animate: false });
    if (commit) {
      this.#committedIndex = index;
      this.#previewIndex = null;
    }
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.#selectMedia(event.detail.index, true);
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
